import { useEffect, useRef, useState } from "react";
import { FiPlay, FiPause, FiMusic } from "react-icons/fi";
import { STYLES, suitedStyles, TAKE_SECONDS, styleById } from "../../lib/studio/styles";
import { KEYS, keyLabel, keyOf } from "../../lib/backing/key";
import { takeGrid, bandShift } from "../../lib/backing/grid";
import { studioRequest } from "../studio/api";
import { progressOf } from "../studio/useStudioJobs";
import { readTake, readBand, studioTake, createTakeMix } from "./takeMix";
import styles from "./social.module.css";

const clock = (seconds) => `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, "0")}`;
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
// The studio's parts, as the band step says them: what the take kept, and what the band added.
const KEPT = { drums: "your drums", bass: "your bass", other: "your playing", vocals: "your voice" }, ADDED = { drums: "drums", bass: "bass", other: "the rest of the band" };
const words = (list) => list.length > 1 ? `${list.slice(0, -1).join(", ")} and ${list.at(-1)}` : list[0] || "";

// A band for a take played to a beat, before it's posted. TrueChops hears where the take's beat is
// (src/lib/backing/grid.js) and what key it's in (key.js), and picks a style for it; the studio
// builds a band around the take in that tempo, meter and key, two versions at a time; and each is
// lined up with the take (the band's own beat, heard, moved onto the take's). Hear one with the
// video, set its level, then write the video with it (takeMix.js). `take`: { blob, url, seconds,
// countIn (seconds into the recording the count-in began), beat: { tempo, beats } }; `idea`: what
// the take goes with ({ keySig, words }). `onUse({ blob, extension, label })`.
export default function BandForTake({ take, idea = {}, formats, onUse, onBack }) {
  const video = useRef(null), mix = useRef(null), heard = useRef(null), alive = useRef(true), shown = useRef(0);
  const [state, setState] = useState("listening"), [error, setError] = useState(""), [grid, setGrid] = useState(null), [heardKey, setHeardKey] = useState(null), [key, setKey] = useState("");
  const [style, setStyle] = useState(""), [picked, setPicked] = useState(""), [description, setDescription] = useState(""), [job, setJob] = useState(null);
  const [bands, setBands] = useState([]), [chosen, setChosen] = useState(-1), [level, setLevel] = useState(1), [time, setTime] = useState(0), [playing, setPlaying] = useState(false), [progress, setProgress] = useState(0);
  const { tempo, beats } = take.beat;

  // Listening to the take: where its beat is, and its key.
  useEffect(() => {
    alive.current = true;
    (async () => {
      try {
        const read = await readTake(take.blob);
        if (!alive.current) return;
        const found = takeGrid(read.samples, read.rate, { tempo, near: take.countIn + (beats * 60) / tempo });
        const inKey = keyOf(read.samples.subarray(Math.floor(Math.max(0, found.downbeat) * read.rate)), read.rate, { signature: idea.keySig || null });
        const suited = suitedStyles({ tempo, minor: Boolean(inKey?.minor), swing: found.swing, words: idea.words || "" });
        heard.current = read;
        mix.current = createTakeMix({ video: video.current, take: read.buffer, onEnd: () => setPlaying(false),
          onTime: (seconds) => { if (Math.abs(seconds - shown.current) >= 0.1 || seconds === 0) { shown.current = seconds; setTime(seconds); } } });
        setGrid(found); setHeardKey(inKey); setKey(inKey?.id || ""); setStyle(suited[0]); setPicked(suited[0]); setState("ready");
      } catch (failure) { if (alive.current) { setError(failure.message); setState("error"); } }
    })();
    return () => { alive.current = false; mix.current?.close(); mix.current = null; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function choose(index, list = bands) {
    setChosen(index);
    const band = list[index];
    mix.current?.setBand(band ? band.buffer : null, band ? band.at : 0);
  }
  async function make() {
    setError("");
    const seconds = Math.min(TAKE_SECONDS, heard.current.buffer.duration - grid.downbeat);
    if (seconds < 2) { setError("Play for a few bars after the count-in, then try again."); return; }
    if (style === "custom" && description.trim().length < 3) { setError("Describe the band you'd like."); return; }
    setState("making");
    try {
      const file = await studioTake(heard.current.buffer, grid.downbeat, seconds), round = Math.floor(bands.length / 2);
      const query = { tempo: Math.round(tempo), beats, seconds: Math.ceil(seconds), count: 2, seed: 7 + round * 2, ...(key ? { key } : {}), ...(style === "custom" ? { description: description.trim() } : { style }) };
      let { job: current } = await studioRequest("take-band", { method: "POST", body: file, query });
      setJob(current);
      while (current.status !== "done" && current.status !== "failed") {
        await wait(2500);
        if (!alive.current) return;
        ({ job: current } = await studioRequest(`jobs/${current.id}`));
        setJob(current);
      }
      if (current.status === "failed") throw new Error(current.error || "The studio couldn't make a band for this take.");
      const label = style === "custom" ? description.trim() : styleById(style)?.label || "Band";
      // Each band lined up: its own beat, heard, put on the take's bar 1.
      const made = await Promise.all(current.results.filter((result) => result.band).map(async (result, index) => {
        const read = await readBand(result.url), shift = bandShift(read.samples, read.rate, { tempo });
        return { id: `${current.id}:${result.name}`, label: `${label} ${round * 2 + index + 1}`, buffer: read.buffer, at: grid.downbeat - (shift ?? 0), kept: result.kept || [] };
      }));
      if (!alive.current) return;
      if (!made.length) throw new Error("The studio made nothing to add to this take.");
      const list = [...bands, ...made];
      setBands(list); choose(bands.length, list); setState("ready");
    } catch (failure) { if (alive.current) { setError(failure.message); setState("ready"); } }
  }
  function toggle() {
    if (!mix.current) return;
    if (mix.current.playing) { mix.current.pause(); setPlaying(false); } else { mix.current.play(time >= take.seconds - 0.1 ? 0 : time); setPlaying(true); }
  }
  async function use() {
    setError(""); setState("rendering"); setPlaying(false); setProgress(0);
    try { const made = await mix.current.render({ formats, onProgress: setProgress }); if (alive.current) onUse({ ...made, label: bands[chosen]?.label || "a band" }); }
    catch (failure) { if (alive.current) { setError(failure.message || "This browser couldn't put the video together."); setState("ready"); } }
  }

  const band = bands[chosen], kept = band?.kept || [], added = ["drums", "bass", "other"].filter((part) => !kept.includes(part));
  const busy = state === "making" || state === "rendering";
  return <div className={styles.bandStep} role="group" aria-label="Add a band">
    <div className={styles.recorderStage}>
      <video ref={video} src={take.url} playsInline muted preload="auto" aria-label="Your take, with the band" />
      {state === "listening" && <p className={styles.recorderMessage} role="status">Listening to your take…</p>}
      {state === "rendering" && <p className={styles.bandRendering} role="status">Putting your video together… {Math.round(progress * 100)}%<small>Keep this page open.</small></p>}
    </div>
    {state === "error" && <p className={styles.error} role="alert">{error}</p>}
    {grid && <>
      <div className={styles.bandControls}>
        <button type="button" onClick={toggle} disabled={busy} aria-label={playing ? "Pause" : "Play"}>{playing ? <FiPause aria-hidden="true" /> : <FiPlay aria-hidden="true" />}</button>
        <input type="range" min={0} max={take.seconds} step={0.1} value={Math.min(time, take.seconds)} disabled={busy} aria-label="Position" onChange={(event) => mix.current?.seek(Number(event.target.value))} />
        <span>{clock(time)} / {clock(take.seconds)}</span>
      </div>
      <p className={styles.hint}>{Math.round(tempo)} BPM in {beats}{heardKey ? ` · sounds like ${heardKey.label}` : ""}{grid.heard ? "" : " · your beat wasn't clear, so the band starts with the count-in"}.</p>
      {bands.length > 0 && <fieldset className={styles.bandChoice} disabled={busy}>
        <legend>Band</legend>
        <div className={styles.bandChips}>
          <button type="button" aria-pressed={chosen === -1} onClick={() => choose(-1)}>No band</button>
          {bands.map((item, index) => <button key={item.id} type="button" aria-pressed={chosen === index} onClick={() => choose(index)}>{item.label}</button>)}
        </div>
        {band && <label className={styles.bandLevel}>Band level<input type="range" min={0} max={1.5} step={0.05} value={level} onChange={(event) => { const value = Number(event.target.value); setLevel(value); mix.current?.setLevel(value); }} /></label>}
        {band && <p className={styles.hint}>Keeps {words(kept.map((part) => KEPT[part]).filter(Boolean)) || "your take"} as you played it, and adds {words(added.map((part) => ADDED[part]))}.</p>}
      </fieldset>}
      <fieldset className={styles.bandChoice} disabled={busy}>
        <legend>{bands.length ? "Make more" : "The band"}</legend>
        <label>Key<select value={key} onChange={(event) => setKey(event.target.value)}>
          <option value="">Any key (drums, or no key)</option>
          {KEYS.map((item) => <option key={item.id} value={item.id}>{item.label}{heardKey?.id === item.id ? " (heard)" : ""}</option>)}
        </select></label>
        <div className={styles.bandChips} role="group" aria-label="Style">
          {STYLES.map((item) => <button key={item.id} type="button" aria-pressed={style === item.id} onClick={() => setStyle(item.id)}>{item.label}{picked === item.id ? " ★" : ""}</button>)}
          <button type="button" aria-pressed={style === "custom"} onClick={() => setStyle("custom")}>Describe it…</button>
        </div>
        {style === "custom" && <label>The band<input value={description} maxLength={160} onChange={(event) => setDescription(event.target.value)} placeholder="latin jazz combo, piano montunos, congas" /></label>}
        <p className={styles.hint}>★ TrueChops&rsquo;s pick for this take{key ? `, in ${keyLabel(key)}` : ""}. Two versions take about a minute{take.seconds - grid.downbeat > TAKE_SECONDS ? `; the band plays the first ${TAKE_SECONDS} seconds` : ""}.</p>
        <button type="button" className={styles.primary} onClick={make} disabled={busy}><FiMusic aria-hidden="true" />{state === "making" ? progressOf(job) || "Sending your take…" : bands.length ? "Make two more" : "Make the band"}</button>
      </fieldset>
    </>}
    {error && state !== "error" && <p className={styles.error} role="alert">{error}</p>}
    <div className={styles.formActions}>
      <button type="button" onClick={onBack} disabled={state === "rendering"}>Back to my take</button>
      {band && <button type="button" className={styles.primary} onClick={use} disabled={busy}>Use it with {band.label}</button>}
    </div>
  </div>;
}
